import { useMedplum } from '@medplum/react'
import { formatHumanName } from '@medplum/core'
import type { DocumentReference, Encounter, Patient, Provenance } from '@medplum/fhirtypes'
import { useMutation } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import { generateCareCoordinatorCDA } from '@/pages/Call/components/soap.utils.ts'
import { getReasonForCallLabel, type CareCoordinatorNoteFormValues } from './careCoordinatorSchema'
import { resolveChartVisitContext } from './progressNoteEncounter.utils'
import {
  BEHUMAN_DOCUMENT_TYPE_SYSTEM,
  CARE_COORDINATOR_ENCOUNTER_CLASS_CODE,
  CARE_COORDINATOR_ENCOUNTER_CLASS_SYSTEM,
} from '@/model/middleware-types'

export function useSubmitCareCoordinatorNote(
  patient: Patient,
  onSubmitted: (reference?: string) => void,
) {
  const medplum = useMedplum()

  return useMutation({
    async mutationFn(values: CareCoordinatorNoteFormValues) {
      if (!patient.id) {
        throw new Error('Patient id is required')
      }
      const context = await resolveChartVisitContext(medplum, patient)

      const profile = medplum.getProfile()
      if (!profile) {
        throw new Error('Signed-in practitioner profile is required')
      }

      const cdaXml = generateCareCoordinatorCDA({
        patient,
        profile,
        values,
        practitionerDisplay: context.practitionerDisplay,
      })

      const endTime = new Date()

      const startTime = new Date(endTime.getTime() - values.callDurationSeconds * 1000)
      const encounterClass = {
        system: CARE_COORDINATOR_ENCOUNTER_CLASS_SYSTEM,
        code: CARE_COORDINATOR_ENCOUNTER_CLASS_CODE,
        display: 'Care Coordinator',
      }

      const createdEncounter = await medplum.createResource<Encounter>({
        resourceType: 'Encounter',
        status: 'finished',
        class: encounterClass,
        subject: {
          reference: `Patient/${patient.id}`,
          display: patient.name?.[0] ? formatHumanName(patient.name[0]) : undefined,
        },
        participant: [
          {
            individual: {
              reference: `Practitioner/${context.practitionerId}`,
              display: context.practitionerDisplay || undefined,
            },
          },
        ],
        period: {
          start: startTime.toISOString(),
          end: endTime.toISOString(),
        },
        meta: {
          compartment: [
            { reference: `Patient/${patient.id}` },
            { reference: `Project/${context.projectId}` },
          ],
        },
      })

      const attachment = await medplum.createAttachment({
        contentType: 'application/cda+xml',
        data: cdaXml,
      })

      const now = new Date().toISOString()
      const doc: DocumentReference = {
        resourceType: 'DocumentReference',
        status: 'current',
        docStatus: 'final',
        extension: [
          {
            url: 'http://behuman.com/fhir/StructureDefinition/care-coordinator-note-data',
            valueString: JSON.stringify(values),
          },
        ],
        type: {
          coding: [
            {
              system: BEHUMAN_DOCUMENT_TYPE_SYSTEM,
              code: 'ProgressNote',
              display: 'Progress Note (Care Coordinator)',
            },
          ],
          text: 'Care Coordinator Note',
        },
        category: [
          {
            coding: [
              {
                system: 'http://behuman.com/fhir/document-category',
                code: 'care-coordinator-note',
                display: 'Care Coordinator Note',
              },
            ],
            text: 'Care Coordinator Note',
          },
        ],
        subject: {
          reference: `Patient/${patient.id}`,
          display: patient.name?.[0] ? formatHumanName(patient.name[0]) : undefined,
        },
        authenticator: {
          reference: `Practitioner/${context.practitionerId}`,
          display: context.practitionerDisplay || undefined,
        },
        // Owning organization of the note. Access policies allow edit only by the owner org
        // (custodian=%providerOrganization), while any patient-linked org may still read it.
        custodian: {
          reference: `Organization/${context.organizationId}`,
        },
        date: now,
        description: `Care Coordinator Note – ${getReasonForCallLabel(values.reasonForCall)}`,
        context: {
          encounter: [{ reference: `Encounter/${createdEncounter.id}` }],
        },
        content: [
          {
            attachment,
            format: {
              system: 'http://behuman.com/fhir/document-format',
              code: 'care-coordinator-note-v1',
              display: 'Care Coordinator Note (C-CDA)',
            },
          },
        ],
        meta: {
          compartment: [
            { reference: `Patient/${patient.id}` },
            { reference: `Project/${context.projectId}` },
          ],
        },
      }

      const createdDoc = await medplum.createResource<DocumentReference>(doc)

      const organization = await medplum.readResource('Organization', context.organizationId)
      const organizationDisplay = organization?.name || 'N/A'
      const compartment = [
        { reference: `Patient/${patient.id}` },
        { reference: `Project/${context.projectId}` },
      ]
      await medplum.createResource<Provenance>({
        resourceType: 'Provenance',
        target: [{ reference: `DocumentReference/${createdDoc.id}`, display: 'Progress Note' }],
        recorded: now,
        agent: [
          {
            type: {
              coding: [
                {
                  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
                  code: 'recorder',
                  display: 'Recorder',
                },
              ],
            },
            who: {
              reference: `Practitioner/${context.practitionerId}`,
              display: context.practitionerDisplay || undefined,
            },
            onBehalfOf: {
              reference: `Organization/${context.organizationId}`,
              display: organizationDisplay,
            },
          },
          {
            type: {
              coding: [
                {
                  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
                  code: 'performer',
                  display: 'Performer',
                },
              ],
            },
            who: {
              reference: `Practitioner/${context.practitionerId}`,
              display: context.practitionerDisplay || undefined,
            },
          },
          {
            type: {
              coding: [
                {
                  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
                  code: 'source',
                  display: 'Source',
                },
              ],
            },
            who: {
              reference: `Organization/${context.organizationId}`,
              display: organizationDisplay,
            },
          },
        ],
        meta: { compartment },
      })

      return `DocumentReference/${createdDoc.id}`
    },
    onSuccess(reference) {
      showNotification({ color: 'green', message: 'Care Coordinator note submitted' })
      onSubmitted(reference)
    },
    onError(err) {
      logError('patient', 'Submit Care Coordinator note', err)
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'Could not submit Care Coordinator note',
      })
    },
  })
}
